import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Waves, BarChart3, Smile, ListChecks, Brain, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';
import { SkillProgressChart } from '@/components/VideoAnalysis';

const COMFORT_MAP = { very_happy: 5, happy: 4, neutral: 3, uncomfortable: 2, upset: 1 };
const COMFORT_LABELS = { 5: 'Loves it!', 4: 'Happy', 3: 'Okay', 2: 'Uneasy', 1: 'Upset' };

const CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const CHART_TICK = { fill: '#64748B', fontSize: 12 };
const CHART_TICK_SM = { fill: '#64748B', fontSize: 10 };
const CHART_TOOLTIP = { borderRadius: '12px', border: '1px solid #E2E8F0' };
const DOT_CYAN = { fill: '#22D3EE', r: 5 };
const DOT_PURPLE = { fill: '#A855F7', r: 5 };
const DOT_SM = { r: 4 };
const DOT_DEFAULT = { r: 5 };
const DOMAIN_40 = [0, 40];
const DOMAIN_5 = [0, 5];
const TICKS_5 = [1, 2, 3, 4, 5];
const TICKS_5_WITH_0 = [0, 1, 2, 3, 4, 5];
const TICKS_BINARY = [0, 1];
const CONFIDENCE_CLASSES = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
};

const INTERVENTION_QUESTIONS = [
  { key: 'q1', label: "Child's response to verbal instructions" },
  { key: 'q2', label: "Effectiveness of visual supports used" },
  { key: 'q3', label: "Child's ability to manage transitions" },
  { key: 'q4', label: "Appropriateness of sensory environment" },
  { key: 'q5', label: "Effectiveness of reinforcement strategies" },
  { key: 'q6', label: "Child's engagement with instructor" },
  { key: 'q7', label: "Child's emotional regulation" },
  { key: 'q8', label: "Session pacing effectiveness" },
];

const CRITICAL_THINKING_POINTS = [
  { key: 'p1', label: "Observes and notices details about objects/environment" },
  { key: 'p2', label: "Makes predictions about what might happen" },
  { key: 'p3', label: "Shows cause-and-effect understanding" },
  { key: 'p4', label: "Attempts independent problem-solving" },
  { key: 'p5', label: "Communicates reasoning (verbal or non-verbal)" },
];

const COLORS = ['#22D3EE', '#22C55E', '#FACC15', '#A855F7', '#EF4444', '#F97316', '#06B6D4', '#EC4899'];

export default function ProgressPage() {
  const { childId } = useParams();
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('intervention');
  const [selectedQuestion, setSelectedQuestion] = useState('all');
  const [selectedThinking, setSelectedThinking] = useState('all');
  const [selectedCriteria, setSelectedCriteria] = useState('all');
  const [loading, setLoading] = useState(true);
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [skillProgress, setSkillProgress] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await api.get(`/reports/progress?child_id=${childId}`);
        setReport(res.data);
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
    };
    const fetchSkillProgress = async () => {
      try {
        const res = await api.get(`/children/${childId}/skill-progress`);
        setSkillProgress(res.data.skills || {});
      } catch { /* silent */ }
    };
    fetchReport();
    fetchSkillProgress();
  }, [childId]);

  const generateAiInterpretation = async () => {
    setAiLoading(true);
    try {
      const res = await api.get(`/children/${childId}/ai-insights`);
      setAiInterpretation(res.data);
      toast.success('AI interpretation generated!');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error('AI budget exceeded. Add balance in Profile > Universal Key.');
      else if (status === 504) toast.error('AI is taking too long. Please try again in a moment.');
      else toast.error('Failed to generate AI interpretation');
    }
    setAiLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;
  if (!report) return <p className="text-center text-slate-400 py-12">No data available</p>;

  // Intervention chart data
  const interventionData = report.assessments.map((a, idx) => {
    const session = report.sessions.find(s => s.session_id === a.session_id);
    const entry = { name: session ? `S${session.session_number}` : `#${idx + 1}`, total: a.intervention_total };
    if (a.intervention_scores) {
      Object.entries(a.intervention_scores).forEach(([key, val]) => { entry[key] = val; });
    }
    return entry;
  });

  // Critical thinking chart data
  const criticalThinkingData = report.assessments.map((a, idx) => {
    const session = report.sessions.find(s => s.session_id === a.session_id);
    const entry = { name: session ? `S${session.session_number}` : `#${idx + 1}`, total: a.critical_thinking_total };
    if (a.critical_thinking_checks) {
      Object.entries(a.critical_thinking_checks).forEach(([key, val]) => { entry[key] = val ? 1 : 0; });
    }
    return entry;
  });

  // Comfort chart data
  const comfortBySession = {};
  report.comfort_ratings.forEach(r => {
    if (!comfortBySession[r.session_id]) comfortBySession[r.session_id] = [];
    comfortBySession[r.session_id].push(r);
  });
  const comfortData = report.sessions.map(s => {
    const ratings = comfortBySession[s.session_id] || [];
    const avg = ratings.length > 0 ? ratings.reduce((sum, r) => sum + (COMFORT_MAP[r.rating] || 3), 0) / ratings.length : null;
    return { name: `S${s.session_number}`, comfort: avg ? parseFloat(avg.toFixed(1)) : null };
  }).filter(d => d.comfort !== null);

  // Criteria chart data - compute dynamic max levels
  const criteriaMap = {};
  report.criteria_ratings.forEach(r => {
    if (!criteriaMap[r.criteria_id]) criteriaMap[r.criteria_id] = [];
    criteriaMap[r.criteria_id].push(r);
  });

  // Compute the max level across all criteria for Y-axis
  const getMaxLevel = () => {
    if (selectedCriteria !== 'all') {
      const criteria = report.criteria.find(c => c.criteria_id === selectedCriteria);
      return criteria?.levels?.length || 5;
    }
    let max = 5;
    report.criteria.forEach(c => {
      const levelCount = c.levels?.length || 5;
      if (levelCount > max) max = levelCount;
    });
    return max;
  };

  const maxLevel = getMaxLevel();
  const levelTicks = Array.from({ length: maxLevel }, (_, i) => i + 1);

  // Build level label lookup for tooltip
  const getLevelLabel = (criteriaId, level) => {
    const criteria = report.criteria.find(c => c.criteria_id === criteriaId);
    if (criteria?.levels && criteria.levels[level - 1]) {
      return criteria.levels[level - 1].label || `Level ${level}`;
    }
    return `Level ${level}`;
  };

  const getCriteriaChartData = () => {
    if (selectedCriteria === 'all') {
      return report.sessions.map(s => {
        const entry = { name: `S${s.session_number}` };
        report.criteria.forEach(c => {
          const rating = report.criteria_ratings.find(r => r.criteria_id === c.criteria_id && r.session_id === s.session_id);
          entry[c.title] = rating ? rating.level : null;
        });
        return entry;
      });
    }
    const ratings = criteriaMap[selectedCriteria] || [];
    return ratings.map(r => {
      const session = report.sessions.find(s => s.session_id === r.session_id);
      return { name: session ? `S${session.session_number}` : '?', level: r.level };
    });
  };

  const getQuestionData = (qKey) => interventionData.map(d => ({ name: d.name, score: d[qKey] ?? null })).filter(d => d.score !== null);
  const getThinkingData = (pKey) => criticalThinkingData.map(d => ({ name: d.name, observed: d[pKey] ?? null })).filter(d => d.observed !== null);

  const TabButton = ({ value, label, isActive, onClick }) => (
    <button onClick={() => onClick(value)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${isActive ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`filter-${value}`}>
      {label}
    </button>
  );

  // Custom tooltip for criteria
  const CriteriaTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg">
        <p className="text-xs font-bold text-slate-600 mb-1">{label}</p>
        {payload.map((p, i) => {
          const criteriaId = selectedCriteria !== 'all' ? selectedCriteria : report.criteria.find(c => c.title === p.dataKey)?.criteria_id;
          const levelLabel = criteriaId ? getLevelLabel(criteriaId, p.value) : `Level ${p.value}`;
          return (
            <p key={`${p.dataKey}-${p.value}-${i}`} className="text-xs" style={{ color: p.color }}>
              {p.name || p.dataKey}: Level {p.value} — {levelLabel}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn" data-testid="progress-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
          {report.child?.name}'s Progress
        </h1>
        <p className="text-slate-500">{report.sessions.length} sessions, {report.assessments.length} assessments</p>
      </div>

      {/* 32-Session Program Tracker */}
      {(() => {
        const totalSessions = report.sessions.length;
        const phases = [
          { name: 'Baseline', start: 1, end: 7, color: 'bg-[#22D3EE]' },
          { name: 'Phase 1', start: 8, end: 12, color: 'bg-[#8B5CF6]' },
          { name: 'Phase 2', start: 13, end: 17, color: 'bg-[#F59E0B]' },
          { name: 'Phase 3', start: 18, end: 22, color: 'bg-[#10B981]' },
          { name: 'Phase 4', start: 23, end: 27, color: 'bg-[#EF4444]' },
          { name: 'Phase 5', start: 28, end: 32, color: 'bg-[#1E1B4B]' },
        ];
        const currentPhase = phases.find(p => totalSessions >= p.start && totalSessions <= p.end) || (totalSessions > 32 ? phases[5] : phases[0]);
        const pct = Math.min((totalSessions / 32) * 100, 100);
        return (
          <Card className="rounded-2xl border-slate-100 mb-6" data-testid="program-tracker">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Waves className="w-4 h-4 text-[#22D3EE]" />
                  <span className="text-sm font-bold text-slate-700">32-Session Program</span>
                </div>
                <Badge className={`rounded-full text-white text-xs ${currentPhase.color}`}>
                  {totalSessions <= 7 ? 'Baseline' : totalSessions <= 32 ? currentPhase.name : 'Complete'}
                </Badge>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3 mb-2">
                <div className={`h-3 rounded-full transition-all ${currentPhase.color}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-400 px-0.5">
                {phases.map(p => (
                  <span key={p.name} className={totalSessions >= p.start ? 'text-slate-600 font-semibold' : ''}>
                    {p.name}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">{totalSessions} of 32 sessions completed ({Math.round(pct)}%)</p>
            </CardContent>
          </Card>
        );
      })()}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-100 rounded-2xl p-1 h-auto gap-1 flex-wrap" data-testid="progress-tabs">
          <TabsTrigger value="intervention" className="rounded-xl data-[state=active]:bg-[#22D3EE] data-[state=active]:text-white font-semibold gap-2">
            <BarChart3 className="w-4 h-4" /> Intervention
          </TabsTrigger>
          <TabsTrigger value="critical" className="rounded-xl data-[state=active]:bg-[#A855F7] data-[state=active]:text-white font-semibold gap-2">
            <ListChecks className="w-4 h-4" /> Critical Thinking
          </TabsTrigger>
          <TabsTrigger value="comfort" className="rounded-xl data-[state=active]:bg-[#22D3EE] data-[state=active]:text-white font-semibold gap-2">
            <Smile className="w-4 h-4" /> Skill Comfort
          </TabsTrigger>
          <TabsTrigger value="criteria" className="rounded-xl data-[state=active]:bg-[#22D3EE] data-[state=active]:text-white font-semibold gap-2">
            <ListChecks className="w-4 h-4" /> Custom Criteria
          </TabsTrigger>
          <TabsTrigger value="ai" className="rounded-xl data-[state=active]:bg-[#1E1B4B] data-[state=active]:text-white font-semibold gap-2">
            <Brain className="w-4 h-4" /> AI Interpretation
          </TabsTrigger>
          <TabsTrigger value="video_ai" className="rounded-xl data-[state=active]:bg-purple-500 data-[state=active]:text-white font-semibold gap-2">
            <Sparkles className="w-4 h-4" /> Video AI
          </TabsTrigger>
        </TabsList>

        {/* Intervention Chart */}
        <TabsContent value="intervention">
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="intervention-chart">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle style={{ fontFamily: 'Nunito' }}>Intervention Score Over Time</CardTitle>
                  <p className="text-sm text-slate-500">Total score out of 40 (8 questions, 1-5 each)</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <TabButton value="all" label="Overall Total" isActive={selectedQuestion === 'all'} onClick={setSelectedQuestion} />
                  {INTERVENTION_QUESTIONS.map((q, i) => (
                    <TabButton key={q.key} value={q.key} label={`Q${i + 1}`} isActive={selectedQuestion === q.key} onClick={setSelectedQuestion} />
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {interventionData.length === 0 && (
                <p className="text-center text-slate-400 py-12">No assessment data yet</p>
              )}
              {interventionData.length > 0 && selectedQuestion === 'all' && (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={interventionData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={CHART_TICK} />
                    <YAxis domain={DOMAIN_40} tick={CHART_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP} />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#22D3EE" strokeWidth={3} dot={DOT_CYAN} name="Intervention Total (/40)" />
                  </LineChart>
                </ResponsiveContainer>
              )}
              {interventionData.length > 0 && selectedQuestion !== 'all' && (
                <div>
                  <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                    <p className="text-sm font-semibold text-slate-700">Q{INTERVENTION_QUESTIONS.findIndex(q => q.key === selectedQuestion) + 1}: {INTERVENTION_QUESTIONS.find(q => q.key === selectedQuestion)?.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Score 1-5 per session</p>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getQuestionData(selectedQuestion)} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={CHART_TICK} />
                      <YAxis domain={DOMAIN_5} ticks={TICKS_5} tick={CHART_TICK} />
                      <Tooltip contentStyle={CHART_TOOLTIP} />
                      <Line type="monotone" dataKey="score" stroke={COLORS[INTERVENTION_QUESTIONS.findIndex(q => q.key === selectedQuestion) % COLORS.length]} strokeWidth={3} dot={DOT_DEFAULT} name="Score" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Critical Thinking Chart */}
        <TabsContent value="critical">
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="critical-thinking-chart">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle style={{ fontFamily: 'Nunito' }}>Critical Thinking Over Time</CardTitle>
                  <p className="text-sm text-slate-500">Total out of 5 (Yes/No per point)</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <TabButton value="all" label="Overall Total" isActive={selectedThinking === 'all'} onClick={setSelectedThinking} />
                  {CRITICAL_THINKING_POINTS.map((p, i) => (
                    <TabButton key={p.key} value={p.key} label={`P${i + 1}`} isActive={selectedThinking === p.key} onClick={setSelectedThinking} />
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {criticalThinkingData.length === 0 && (
                <p className="text-center text-slate-400 py-12">No assessment data yet</p>
              )}
              {criticalThinkingData.length > 0 && selectedThinking === 'all' && (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={criticalThinkingData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={CHART_TICK} />
                    <YAxis domain={DOMAIN_5} ticks={TICKS_5_WITH_0} tick={CHART_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP} />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#A855F7" strokeWidth={3} dot={DOT_PURPLE} name="Critical Thinking (/5)" />
                  </LineChart>
                </ResponsiveContainer>
              )}
              {criticalThinkingData.length > 0 && selectedThinking !== 'all' && (
                <div>
                  <div className="mb-4 p-3 bg-purple-50 rounded-xl">
                    <p className="text-sm font-semibold text-slate-700">P{CRITICAL_THINKING_POINTS.findIndex(p => p.key === selectedThinking) + 1}: {CRITICAL_THINKING_POINTS.find(p => p.key === selectedThinking)?.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Yes (1) / No (0) per session</p>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getThinkingData(selectedThinking)} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={CHART_TICK} />
                      <YAxis domain={TICKS_BINARY} ticks={TICKS_BINARY} tickFormatter={(v) => v === 1 ? 'Yes' : 'No'} tick={CHART_TICK} />
                      <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => [v === 1 ? 'Yes' : 'No', 'Observed']} />
                      <Bar dataKey="observed" fill="#A855F7" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comfort Chart */}
        <TabsContent value="comfort">
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="comfort-chart">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Nunito' }}>Skill Comfort Over Time</CardTitle>
              <p className="text-sm text-slate-500">Average comfort rating per session (1=Upset to 5=Loves it!)</p>
            </CardHeader>
            <CardContent>
              {comfortData.length === 0 ? (
                <p className="text-center text-slate-400 py-12">No comfort ratings yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={comfortData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={CHART_TICK} />
                    <YAxis domain={DOMAIN_5} ticks={TICKS_5} tickFormatter={(v) => COMFORT_LABELS[v] || v} tick={CHART_TICK_SM} />
                    <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => [`${v} (${COMFORT_LABELS[Math.round(v)] || ''})`, 'Comfort']} />
                    <Bar dataKey="comfort" fill="#22D3EE" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Criteria Chart - Dynamic Y-axis */}
        <TabsContent value="criteria">
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="criteria-chart">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle style={{ fontFamily: 'Nunito' }}>Custom Criteria Progress</CardTitle>
                  <p className="text-sm text-slate-500">Level progression (1-{maxLevel}) for co-created criteria</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <TabButton value="all" label="All Criteria" isActive={selectedCriteria === 'all'} onClick={setSelectedCriteria} />
                  {report.criteria.map(c => (
                    <TabButton key={c.criteria_id} value={c.criteria_id} label={c.title} isActive={selectedCriteria === c.criteria_id} onClick={setSelectedCriteria} />
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {report.criteria.length === 0 ? (
                <p className="text-center text-slate-400 py-12">No custom criteria created yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={getCriteriaChartData()} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={CHART_TICK} />
                    <YAxis domain={[0, maxLevel]} ticks={levelTicks} tick={CHART_TICK} />
                    <Tooltip content={<CriteriaTooltip />} />
                    <Legend />
                    {selectedCriteria === 'all' ? (
                      report.criteria.map((c, i) => (
                        <Line key={c.criteria_id} type="monotone" dataKey={c.title} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={DOT_SM} connectNulls />
                      ))
                    ) : (
                      <Line type="monotone" dataKey="level" stroke="#22D3EE" strokeWidth={3} dot={DOT_CYAN} name="Level" />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Interpretation Tab */}
        <TabsContent value="ai">
          <div className="space-y-4" data-testid="ai-interpretation-tab">
            {/* AI Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="ai-interpretation-disclaimer">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">AI-Generated Interpretation - For Reference Only</p>
                  <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                    This interpretation is generated by artificial intelligence and is intended as a supplementary tool only.
                    Do not rely solely on this information for making decisions about a child's program.
                    AI interpretations may be inaccurate or incomplete. Always use your professional judgment
                    and direct observations when assessing progress.
                  </p>
                </div>
              </div>
            </div>

            {aiInterpretation && !aiInterpretation.parsing_error ? (
              <div className="space-y-4">
                {/* Overall Summary */}
                {aiInterpretation.overall_summary && (
                  <Card className="rounded-3xl bg-gradient-to-br from-[#1E1B4B] to-[#312E81] text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Brain className="w-5 h-5 text-[#22D3EE]" />
                        <h3 className="font-bold text-lg" style={{ fontFamily: 'Nunito' }}>AI Progress Interpretation</h3>
                      </div>
                      <p className="text-slate-200 text-sm leading-relaxed">{aiInterpretation.overall_summary}</p>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* What Works */}
                  {aiInterpretation.what_works?.length > 0 && (
                    <Card className="rounded-3xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-green-600">
                          <Sparkles className="w-4 h-4" /> What's Working
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {aiInterpretation.what_works.map((item, i) => (
                            <li key={`works-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" />{item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* What to Avoid */}
                  {aiInterpretation.what_to_avoid?.length > 0 && (
                    <Card className="rounded-3xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" /> Areas of Caution
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {aiInterpretation.what_to_avoid.map((item, i) => (
                            <li key={`avoid-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                              <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 shrink-0" />{item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Next Session Focus */}
                {aiInterpretation.next_session_focus?.length > 0 && (
                  <Card className="rounded-3xl border-[#22D3EE]/20 bg-[#ECFEFF]/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-[#0E7490]">
                        <BarChart3 className="w-4 h-4" /> Suggested Next Focus Areas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {aiInterpretation.next_session_focus.map((item, i) => (
                          <div key={`focus-${i}`} className="flex items-start gap-3 p-3 bg-white rounded-xl">
                            <Badge className="rounded-full bg-[#22D3EE] text-white text-xs shrink-0">{i + 1}</Badge>
                            <p className="text-sm text-slate-600">{item}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Patterns */}
                {aiInterpretation.patterns_observed?.length > 0 && (
                  <Card className="rounded-3xl">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Observed Patterns</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {aiInterpretation.patterns_observed.map((p, i) => (
                          <div key={`pattern-${i}`} className="p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <p className="text-sm font-semibold text-slate-800">{p.pattern}</p>
                              <Badge className={`rounded-full shrink-0 text-xs ${CONFIDENCE_CLASSES[p.confidence] || 'bg-slate-100 text-slate-500'}`}>{p.confidence}</Badge>
                            </div>
                            <p className="text-sm text-slate-500">{p.recommendation}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {aiInterpretation.generated_at && (
                  <p className="text-xs text-slate-400 text-center">Generated {new Date(aiInterpretation.generated_at).toLocaleString()}</p>
                )}
              </div>
            ) : (
              <Card className="rounded-3xl border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Brain className="w-16 h-16 text-slate-300 mb-4" />
                  <p className="text-lg font-bold text-slate-400 mb-2" style={{ fontFamily: 'Nunito' }}>AI Progress Interpretation</p>
                  <p className="text-sm text-slate-400 text-center max-w-md mb-4">
                    Get an AI-powered analysis of this child's progress data, patterns, and recommended focus areas.
                  </p>
                  <Button
                    onClick={generateAiInterpretation}
                    disabled={aiLoading}
                    className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
                    data-testid="generate-ai-interpretation-btn"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {aiLoading ? 'Analyzing...' : 'Generate AI Interpretation'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Video AI Analysis Tab */}
        <TabsContent value="video_ai">
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="video-ai-progress">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Nunito' }}>Video AI Skill Analysis</CardTitle>
              <p className="text-sm text-slate-500">AI-analyzed scores from video recordings across sessions</p>
            </CardHeader>
            <CardContent>
              {skillProgress && Object.keys(skillProgress).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(skillProgress).map(([skillName, dataPoints]) => (
                    <div key={skillName}>
                      <h4 className="text-sm font-bold text-slate-700 mb-2">{skillName}</h4>
                      <SkillProgressChart skillData={dataPoints} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No video analyses yet.</p>
                  <p className="text-xs mt-1">Go to the Storyboard page and tap "AI Skill Analysis" on any video to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
